import { useMemo, useState } from "react";

import { BudgetsQuery } from "@/__generated__/graphql";
import { useAppRouteNavigation } from "@/app/app/_/routing";
import { SKELETON_PIECE_CLASS } from "@/components/molecules/SelectableListItemRow";
import { SimpleEnum } from "@/components/molecules/SimpleEnum";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";
import { Mutations } from "@/constants";
import { EnumColors } from "@/constants/values";
import { useBackendMutation } from "@/hooks/useBackendMutation";
import { useEnumColor } from "@/hooks/useEnumColor";
import { State } from "@/state";
import { Utils } from "@/utils";

export type BudgetViewBudget = NonNullable<
  NonNullable<BudgetsQuery["budgets"]["data"]>[number]
>;
export type BudgetViewMonthData = BudgetViewBudget["month"];
export type BudgetViewLineMonth = BudgetViewMonthData["lines"][number];
type BudgetViewEnumColor = (typeof EnumColors)[keyof typeof EnumColors];

/**
 * How far along something is, in one of the app's colours: its text colour on
 * its own tint, as its chip is.
 */
export function BudgetViewBar({
  value,
  enumColor,
  height = 6,
}: {
  value: number;
  enumColor: BudgetViewEnumColor;
  height?: number;
}) {
  const fill = useEnumColor(enumColor);
  const clamp = (share: number) => Math.min(Math.max(share, 0), 1) * 100;
  return (
    <div aria-hidden className="w-full">
      <div
        className="w-full overflow-hidden"
        style={{
          height,
          borderRadius: height,
          backgroundColor: fill.backgroundColor,
        }}
      >
        <div
          className="h-full"
          style={{
            width: `${clamp(value)}%`,
            borderRadius: height,
            backgroundColor: fill.color,
          }}
        />
      </div>
    </div>
  );
}

/** A card of the month: the theme's surface, framed and lifted a little. */
const CARD_CLASS =
  "rounded-lg border border-border bg-card shadow-xs p-3 flex flex-col gap-2 min-w-0";

/** What each row of the month is, in a word a newcomer knows. */
const ROWS: Record<string, string> = {
  NEED: "Needs",
  WANT: "Wants",
  FUTURE: "Saving and debts",
};

/** At most this many things are said to need a look: the rest are on the rows they are in. */
const NUDGES = 2;

/** A typical line's month, for what is drawn while the month loads. */
export function placeholderLine(
  name: string,
  kind: string,
): BudgetViewLineMonth {
  return {
    __typename: "BudgetLineMonthObject",
    amountPlanned: "400.00",
    amountSpent: "120.00",
    amountDue: "0.00",
    amountLeft: "280.00",
    amountSaved: null,
    amountOwed: null,
    amountNeeded: "0.00",
    bills: [],
    line: {
      __typename: "BudgetLineObject",
      id: btoa(`BudgetLineObject:${name}`),
      identifier: name,
      name: "",
      kind,
      bucket: "NEED",
      amountPlanned: "400.00",
      amountCurrency: "CAD",
      amountGoal: kind === "SAVING" ? "2000.00" : null,
      goalBy: null,
      goalMode: "MONTHLY",
      isPaused: false,
      index: 0,
      amountOpening: "0.00",
      chartedAccount: {
        __typename: "ChartedAccountObject",
        id: btoa(`ChartedAccountObject:${name}`),
        identifier: name,
        name,
        path: "5.1.1.",
        parent: null,
      },
      budget: null,
      entity: null,
    } as BudgetViewLineMonth["line"],
  };
}

/** A typical month, for what is drawn while the month loads. */
const PLACEHOLDER_MONTH = (() => {
  const lines = [
    placeholderLine("Rent", "BILL"),
    placeholderLine("Groceries", "EVERYDAY"),
    placeholderLine("Dining out", "EVERYDAY"),
    // The wishlist's, drawn in its skeleton: a wish saved for by a day, and one by the month.
    placeholderLine("Emergency fund", "SAVING"),
    placeholderLine("New laptop", "SAVING"),
  ];
  const bucket = (name: string) => ({
    __typename: "BudgetBucketObject",
    bucket: name,
    amountPlanned: "1200.00",
    amountSpent: "800.00",
    pace: "ON_PACE",
    lines: lines
      .filter((row) => row.line.kind !== "SAVING")
      .map((row) => ({ line: { id: row.line.id } })),
  });
  return {
    month: "2026-01-01",
    daysLeft: 7,
    shareOfMonth: "0.75",
    pace: "ON_PACE",
    buckets: [bucket("NEED"), bucket("WANT")],
    attention: [],
    amountIncomePlanned: "4900.00",
    amountIncome: "4900.00",
    amountBills: "2180.00",
    amountSaving: "1125.00",
    amountSpread: "0.00",
    amountSpent: "1000.00",
    waitingCount: 0,
    amountWaiting: "0.00",
    amountLeft: "240.00",
    amountLeftPlanned: "1240.00",
    amountEverydayPlanned: "800.00",
    amountPerDay: "34.00",
    uncountedCurrencies: [],
    lines,
  } as unknown as BudgetViewMonthData;
})();

/** A typical budget, its month a typical month: what the view draws in skeletons while it is read. */
export const PLACEHOLDER_BUDGET = {
  __typename: "BudgetObject",
  id: btoa("BudgetObject:placeholder"),
  identifier: "placeholder",
  name: "Budget",
  amountCurrency: "CAD",
  amountIncome: "4900.00",
  startsOn: "2026-01-01",
  status: "ACTIVE",
  entity: {
    __typename: "EntityObject",
    id: btoa("EntityObject:placeholder"),
    identifier: "placeholder",
    currency: "CAD",
  },
  month: PLACEHOLDER_MONTH,
} as unknown as BudgetViewBudget;

/**
 * A line's name: a goal's own, else its account's - and nothing where it has
 * neither, never a name made up for it.
 */
export function nameBudgetLine(line: {
  name?: string | null;
  chartedAccount?: { name?: string | null } | null;
}): string {
  return line.name || line.chartedAccount?.name || "";
}

/**
 * A month of the budget, for someone new to budgeting: one number - what is
 * left to spend - and where the month's money went; the one or two things
 * worth a look, if any; then needs, wants, and saving and debts, a row each
 * saying in a word how it is, and opening to its lines.
 */
export function BudgetViewMonth({
  budget,
  isLoading,
}: {
  /** The budget read - or, while it is read, a typical one drawn in skeletons. */
  budget: BudgetViewBudget;
  isLoading: boolean;
}) {
  const month = budget.month;
  // Said only where the budget is not in its business's own currency.
  const currency =
    budget.amountCurrency !== budget.entity.currency
      ? (budget.amountCurrency ?? undefined)
      : undefined;
  const [opened, setOpened] = useState<Set<string>>(new Set());
  const byId = useMemo(
    () => new Map(month.lines.map((row) => [row.line.id, row])),
    [month],
  );
  const future = month.lines.filter(
    (row) => row.line.kind === "SAVING" || row.line.kind === "DEBT",
  );
  const toggle = (key: string) => {
    const next = new Set(opened);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setOpened(next);
  };

  return (
    <div
      data-testid="budget-month-view"
      className="flex-1 min-h-0 overflow-y-auto p-2 sm:p-3 flex flex-col gap-3 w-full max-w-[720px] mx-auto"
    >
      <BudgetViewLeft month={month} currency={currency} isLoading={isLoading} />
      {!isLoading && month.leftover && (
        <BudgetViewLeftover
          budget={budget}
          leftover={month.leftover}
          goals={month.lines.filter((row) => row.line.kind === "SAVING")}
          currency={currency}
        />
      )}
      {!isLoading && month.attention.length > 0 && (
        <BudgetViewAttention
          attention={month.attention.slice(0, NUDGES)}
          byId={byId}
          month={month}
          currency={currency}
        />
      )}
      <section
        aria-label="Where the money goes"
        className={cn(CARD_CLASS, "p-1 gap-0")}
      >
        {month.buckets.map((bucket) => (
          <BudgetViewBucket
            key={bucket.bucket}
            bucket={bucket.bucket}
            amountPlanned={bucket.amountPlanned}
            amountSpent={bucket.amountSpent}
            pace={bucket.pace}
            month={month}
            rows={bucket.lines.flatMap((each) => byId.get(each.line.id) ?? [])}
            isOpen={opened.has(bucket.bucket)}
            onToggle={() => toggle(bucket.bucket)}
            currency={currency}
            isLoading={isLoading}
          />
        ))}
        {!isLoading && future.length > 0 && (
          <BudgetViewBucket
            bucket="FUTURE"
            amountPlanned={month.amountSaving}
            amountSpent={month.amountSaving}
            month={month}
            rows={future}
            isOpen={opened.has("FUTURE")}
            onToggle={() => toggle("FUTURE")}
            currency={currency}
            isLoading={isLoading}
          />
        )}
      </section>
    </div>
  );
}

/**
 * The top of the month: what is left to spend - or how far over - in one
 * number, what that means in a sentence, and one bar of where the money that
 * came in went, each part named under it.
 */
function BudgetViewLeft({
  month,
  currency,
  isLoading,
}: {
  month: BudgetViewMonthData;
  currency?: string;
  isLoading: boolean;
}) {
  const navigation = useAppRouteNavigation();
  const red = useEnumColor(EnumColors.RED);
  const money = (amount: string | number) =>
    Utils.fCurrency(amount, false, false, currency);
  const left = Number(month.amountLeft);
  const name = new Date(`${month.month}T12:00:00`).toLocaleDateString(
    undefined,
    { month: "long" },
  );
  const share = Number(month.shareOfMonth);
  const isNow = share > 0 && share < 1;
  const isPast = share >= 1;
  const headline = isPast
    ? `${name} ended with`
    : isNow
      ? `Left to spend in ${name}`
      : `Planned for ${name}`;
  const meaning = isPast
    ? left < 0
      ? "You spent more than came in."
      : "You spent less than came in."
    : left < 0
      ? "More is planned or spent than comes in."
      : isNow
        ? `About ${money(month.amountPerDay)} a day for the ${month.daysLeft} ${month.daysLeft === 1 ? "day" : "days"} left${month.pace === "FAST" ? " - you're spending a bit fast" : ""}.`
        : "If the month goes as planned.";
  const income = Math.max(
    Number(month.amountIncomePlanned),
    Number(month.amountIncome),
  );
  const parts = [
    { label: "Bills", amount: month.amountBills, enumColor: EnumColors.BLUE },
    {
      label: "Put away",
      amount: month.amountSaving,
      enumColor: EnumColors.GREEN,
    },
    {
      label: "Set aside",
      amount: month.amountSpread,
      enumColor: EnumColors.YELLOW,
    },
    {
      label: isNow ? "Spent so far" : "Spent",
      amount: month.amountSpent,
      enumColor: EnumColors.ORANGE,
    },
    {
      label: "Left",
      amount: String(Math.max(left, 0)),
      enumColor: EnumColors.GREY_1,
    },
  ].filter((part) => Number(part.amount) > 0);

  return (
    <div data-testid="budget-left" className={cn(CARD_CLASS, "gap-2 p-4")}>
      <Typography
        level="body-sm"
        data-testid="budget-headline"
        className="text-muted-foreground"
      >
        <Skeleton loading={isLoading} variant="inline">
          {headline}
        </Skeleton>
      </Typography>
      <div className="flex items-baseline gap-2 flex-wrap">
        <Typography
          level="h2"
          data-testid="budget-left-amount"
          className="tabular-nums"
          style={left < 0 ? { color: red.color } : undefined}
        >
          <Skeleton loading={isLoading} variant="inline">
            {money(Math.abs(left))}
          </Skeleton>
        </Typography>
        <Typography
          level="title-lg"
          data-testid="budget-left-word"
          className="text-muted-foreground"
        >
          <Skeleton loading={isLoading} variant="inline">
            {left < 0 ? "over" : "left"}
          </Skeleton>
        </Typography>
      </div>
      <Typography level="body-md" data-testid="budget-meaning">
        <Skeleton loading={isLoading} variant="inline">
          {meaning}
        </Skeleton>
      </Typography>
      {month.uncountedCurrencies.length > 0 && (
        <Typography
          level="body-sm"
          data-testid="budget-uncounted"
          className="text-muted-foreground/80"
        >
          {`Money in ${month.uncountedCurrencies.join(" and ")} isn't counted here - there's no exchange rate for it yet.`}
        </Typography>
      )}
      <Skeleton
        loading={isLoading}
        variant="rectangular"
        className={cn(SKELETON_PIECE_CLASS, "w-full h-2.5")}
      >
        <BudgetViewWhere
          parts={parts}
          total={Math.max(
            income,
            parts.reduce((sum, part) => sum + Number(part.amount), 0),
          )}
        />
      </Skeleton>
      <div
        data-testid="budget-where"
        className="flex flex-wrap gap-x-4 gap-y-1"
      >
        {parts.map((part) => (
          <BudgetViewWherePart
            key={part.label}
            label={part.label}
            amount={money(part.amount)}
            enumColor={part.enumColor}
            isLoading={isLoading}
          />
        ))}
      </div>
      {month.waitingCount > 0 && (
        <Typography
          level="body-sm"
          data-testid="budget-waiting"
          className="flex items-center gap-1.5 text-muted-foreground"
        >
          {`${month.waitingCount} ${month.waitingCount === 1 ? "payment" : "payments"} (${money(month.amountWaiting)}) not sorted yet - counted as spent`}
          <Typography
            component="button"
            level="body-sm"
            onClick={() => navigation.navigateToView("triage")}
            className="font-semibold cursor-pointer text-foreground p-0 whitespace-nowrap"
          >
            Sort them ›
          </Typography>
        </Typography>
      )}
    </div>
  );
}

/** Where the month's money went, as one bar: each part its share of what came in, in its colour. */
function BudgetViewWhere({
  parts,
  total,
}: {
  parts: {
    label: string;
    amount: string;
    enumColor: BudgetViewEnumColor;
  }[];
  total: number;
}) {
  return (
    <div
      aria-hidden
      className="flex w-full h-2.5 rounded-[10px] overflow-hidden gap-0.5"
    >
      {parts.map((part) => (
        <BudgetViewWhereSegment
          key={part.label}
          share={total > 0 ? Number(part.amount) / total : 0}
          enumColor={part.enumColor}
        />
      ))}
    </div>
  );
}

function BudgetViewWhereSegment({
  share,
  enumColor,
}: {
  share: number;
  enumColor: BudgetViewEnumColor;
}) {
  const colors = useEnumColor(enumColor);
  return (
    <div
      className="shrink-0 min-w-1"
      style={{
        flexBasis: `${share * 100}%`,
        backgroundColor:
          enumColor === EnumColors.GREY_1 ? colors.borderColor : colors.color,
      }}
    />
  );
}

/** A part of the bar, named: its colour, what it is and how much. */
function BudgetViewWherePart({
  label,
  amount,
  enumColor,
  isLoading,
}: {
  label: string;
  amount: string;
  enumColor: BudgetViewEnumColor;
  isLoading: boolean;
}) {
  const colors = useEnumColor(enumColor);
  return (
    <Typography
      level="body-xs"
      data-testid="budget-where-part"
      className="flex items-center gap-1.5 text-muted-foreground tabular-nums"
    >
      <Skeleton
        loading={isLoading}
        variant="rectangular"
        className={SKELETON_PIECE_CLASS}
      >
        <div
          className="size-2 rounded-[50%]"
          style={{
            backgroundColor:
              enumColor === EnumColors.GREY_1
                ? colors.borderColor
                : colors.color,
          }}
        />
      </Skeleton>
      <Skeleton loading={isLoading} variant="inline">
        {`${label} ${amount}`}
      </Skeleton>
    </Typography>
  );
}

/**
 * Needs, wants, or saving and debts, as one row: what it is and, in a word,
 * how it is - a line over, a goal behind, a bit fast, or what is left - and,
 * pressed, its lines.
 */
function BudgetViewBucket({
  bucket,
  amountPlanned,
  amountSpent,
  pace,
  month,
  rows,
  isOpen,
  onToggle,
  currency,
  isLoading,
}: {
  bucket: string;
  amountPlanned: string;
  amountSpent: string;
  pace?: string;
  month: BudgetViewMonthData;
  rows: BudgetViewLineMonth[];
  isOpen: boolean;
  onToggle: () => void;
  currency?: string;
  isLoading: boolean;
}) {
  const money = (amount: string | number) =>
    Utils.fCurrency(amount, false, false, currency);
  const planned = Number(amountPlanned);
  const spent = Number(amountSpent);
  // How it is, as the month says it: its lines over - its goals behind -
  // where the month says any need a look, else the bucket's pace.
  const flagged = rows.filter((row) =>
    month.attention.some(
      (each) =>
        each.line.line.id === row.line.id &&
        each.reason === (bucket === "FUTURE" ? "BEHIND" : "OVER"),
    ),
  );
  const [status, enumColor]: [string, BudgetViewEnumColor] =
    bucket === "FUTURE"
      ? flagged.length
        ? [
            flagged.length === 1 && nameBudgetLine(flagged[0].line)
              ? `${nameBudgetLine(flagged[0].line)} behind`
              : `${flagged.length} ${flagged.length === 1 ? "goal" : "goals"} behind`,
            EnumColors.ORANGE,
          ]
        : [`${money(spent)} put away`, EnumColors.GREEN]
      : flagged.length
        ? [
            flagged.length === 1 && nameBudgetLine(flagged[0].line)
              ? `${nameBudgetLine(flagged[0].line)} over`
              : `${flagged.length} over`,
            EnumColors.RED,
          ]
        : pace === "OVER"
          ? [`${money(spent - planned)} over`, EnumColors.RED]
          : pace === "FAST"
            ? ["A bit fast", EnumColors.ORANGE]
            : [`${money(planned - spent)} left`, EnumColors.GREEN];
  return (
    <section
      aria-label={ROWS[bucket]}
      data-testid="budget-bucket"
      data-bucket={bucket}
      // A line between one row and the next: each row of the card is a bucket.
      className="rounded-md not-first:border-t not-first:border-border"
    >
      <button
        type="button"
        data-testid="budget-bucket-open"
        aria-expanded={isOpen}
        onClick={onToggle}
        disabled={isLoading}
        className="w-full text-left cursor-pointer rounded-md px-3 py-2.5 flex items-center gap-3 hover:bg-muted"
      >
        <Typography level="title-md" className="flex-1 min-w-0">
          {ROWS[bucket]}
        </Typography>
        <div data-testid="budget-bucket-status">
          <Skeleton
            loading={isLoading}
            variant="rectangular"
            className={SKELETON_PIECE_CLASS}
          >
            <SimpleEnum label={status} enumColor={enumColor} size="sm" />
          </Skeleton>
        </div>
        <Typography
          aria-hidden
          level="body-md"
          className={cn(
            "text-muted-foreground/80 [transition:transform_120ms]",
            isOpen ? "[transform:rotate(90deg)]" : "[transform:none]",
          )}
        >
          ›
        </Typography>
      </button>
      {isOpen && (
        <div className="flex flex-col gap-0.5 px-3 pb-2">
          {bucket !== "FUTURE" && (
            <Typography
              level="body-xs"
              data-testid="budget-bucket-amount"
              className="text-muted-foreground/80 mb-1"
            >
              {`${money(spent)} of ${money(planned)} spent`}
            </Typography>
          )}
          {rows.map((row) => (
            <BudgetViewCardLine
              key={row.line.id}
              row={row}
              month={month}
              currency={currency}
              isLoading={isLoading}
            />
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * The one or two things most worth a look this month, the most pressing first -
 * each its line, a sentence and a word: over, with what could cover it; a bill
 * due; nearly used up; a goal falling behind. Pressed, it opens the line.
 */
function BudgetViewAttention({
  attention,
  byId,
  month,
  currency,
}: {
  attention: BudgetViewMonthData["attention"];
  byId: Map<string, BudgetViewLineMonth>;
  month: BudgetViewMonthData;
  currency?: string;
}) {
  const money = (amount: string | number) =>
    Utils.fCurrency(amount, false, false, currency);
  const day = (iso: string, options: Intl.DateTimeFormatOptions) =>
    new Date(iso).toLocaleDateString(undefined, options);
  return (
    <section
      className={cn(CARD_CLASS, "p-1 gap-0")}
      aria-label="Worth a look"
      data-testid="budget-attention"
    >
      {attention.map((each) => {
        const row = byId.get(each.line.line.id);
        if (!row) return null;
        const from = each.fromLine ? byId.get(each.fromLine.line.id) : null;
        const bill = row.bills[0];
        const [chip, enumColor, sentence] =
          each.reason === "OVER"
            ? [
                `${money(each.amount)} over`,
                EnumColors.RED,
                from
                  ? Number(each.amountFrom) >= Number(each.amount)
                    ? `Make up for it from ${nameBudgetLine(from.line)}, which has money left this month`
                    : `${nameBudgetLine(from.line)} has ${money(each.amountFrom)} left that could make up part of it`
                  : "More than you planned, and nothing else has money left to make up for it",
              ]
            : each.reason === "DUE"
              ? [
                  `${money(each.amount)} due`,
                  EnumColors.ORANGE,
                  bill?.dateDue
                    ? `${bill.name} is due ${day(bill.dateDue, { month: "short", day: "numeric" })}`
                    : "A bill is due",
                ]
              : each.reason === "NEARLY"
                ? [
                    `${money(each.amount)} left`,
                    EnumColors.ORANGE,
                    `${Number(each.amount) > 0 ? "Nearly used up" : "All used"}, with ${month.daysLeft} days to go`,
                  ]
                : each.reason === "AHEAD"
                  ? [
                      `${money(each.amount)} ahead`,
                      EnumColors.ORANGE,
                      // The month the server says it is made up by: nothing in its place where it says none.
                      row.coveredOn
                        ? `Spent so far ahead it isn't made up until ${day(`${row.coveredOn}T12:00:00`, { month: "long", year: "numeric" })}`
                        : "Spent far ahead",
                    ]
                  : [
                      `${money(each.amount)} short`,
                      EnumColors.ORANGE,
                      // Its goal and when by, where the line says them: nothing in their place where it does not.
                      `Put away ${money(row.amountNeeded)} a month${row.line.amountGoal != null ? ` to reach ${money(row.line.amountGoal)}` : ""}${row.line.goalBy ? ` by ${day(`${row.line.goalBy}T12:00:00`, { month: "short", year: "numeric" })}` : ""}`,
                    ];
        return (
          <button
            key={`${each.reason}-${row.line.id}`}
            type="button"
            data-testid="budget-attention-line"
            data-line-id={row.line.identifier}
            onClick={() =>
              State.getState().openNodePanel(row.line.id, { autofocus: false })
            }
            className="text-left cursor-pointer rounded-md px-3 py-2 flex items-center gap-3 hover:bg-muted"
          >
            <div className="flex-1 min-w-0">
              <Typography level="body-md" data-testid="budget-attention-name">
                {nameBudgetLine(row.line)}
              </Typography>
              <Typography
                level="body-xs"
                data-testid="budget-attention-sentence"
                className="text-muted-foreground/80"
              >
                {sentence}
              </Typography>
            </div>
            <div data-testid="budget-attention-chip">
              <SimpleEnum label={chip} enumColor={enumColor} size="sm" />
            </div>
          </button>
        );
      })}
    </section>
  );
}

/**
 * One line of a bucket: its name, how far along it is, and what that means - left
 * or over for everyday spending, paid or due for a bill, saved towards its goal,
 * still owed on a debt. Opens the line's panel.
 */
function BudgetViewCardLine({
  row,
  currency,
  isLoading,
}: {
  row: BudgetViewLineMonth;
  month: BudgetViewMonthData;
  currency?: string;
  isLoading: boolean;
}) {
  const activePanelNodeId = State((state) => state.activePanelNodeId);
  const money = (amount: string | number) =>
    Utils.fCurrency(amount, false, false, currency);
  const kind = row.line.kind;
  const planned = Number(row.amountPlanned);
  const spent = Number(row.amountSpent);
  const due = Number(row.amountDue);
  const left = Number(row.amountLeft);

  let progress = 0;
  // In the app's colours for what it means: red over, orange near it or due,
  // green paid or put by, blue on its way, grey for what has nothing to say.
  let barColor: BudgetViewEnumColor = EnumColors.BLUE;
  let figureColor: BudgetViewEnumColor = EnumColors.GREY_1;
  let figure = "";
  let note = "";
  // What a line has saved, or still owes, is said of a goal, a line spread
  // over months and a debt: each read as the month has it, and the line said as
  // everyday spending where the month says none.
  if (
    row.line.isSpread &&
    (kind === "BILL" || kind === "EVERYDAY") &&
    row.amountSaved != null
  ) {
    // Spread over months: what it has built up, or how far it was spent ahead and the month that catches it up.
    const saved = Number(row.amountSaved);
    const had = saved + spent + due;
    progress = had > 0 ? (spent + due) / had : 1;
    if (saved >= 0) {
      barColor = figureColor = EnumColors.GREEN;
      figure = `${money(saved)} saved up`;
      note = `${money(planned)} a month, spread over months`;
    } else {
      barColor = figureColor = EnumColors.BLUE;
      figure = `${money(-saved)} spent ahead`;
      note = row.coveredOn
        ? `covered by ${new Date(`${row.coveredOn}T12:00:00`).toLocaleDateString(undefined, { month: "long" })}`
        : "spent ahead";
    }
  } else if (kind === "SAVING" && row.amountSaved != null) {
    const saved = Number(row.amountSaved);
    // Without a goal, what it has saved alone: no goal made up to measure it by.
    const goal =
      row.line.amountGoal != null ? Number(row.line.amountGoal) : null;
    progress = goal ? saved / goal : 0;
    barColor = figureColor = EnumColors.GREEN;
    figure = goal ? `${money(saved)} of ${money(goal)}` : money(saved);
    note = [
      row.line.goalBy
        ? `by ${new Date(`${row.line.goalBy}T12:00:00`).toLocaleDateString(undefined, { month: "short", year: "numeric" })}`
        : null,
      Number(row.amountNeeded) > planned
        ? `needs ${money(row.amountNeeded)} a month`
        : goal
          ? "on track"
          : null,
      `${money(planned)} a month`,
    ]
      .filter(Boolean)
      .join(" · ");
  } else if (kind === "DEBT" && row.amountOwed != null) {
    const opening = Number(row.line.amountOpening);
    const owed = Number(row.amountOwed);
    progress = opening > 0 ? (opening - owed) / opening : 0;
    barColor = EnumColors.AQUA;
    figure = `${money(owed)} owed`;
    note = `${money(planned)} a month`;
  } else if (kind === "BILL") {
    progress = planned > 0 ? (spent + due) / planned : spent > 0 ? 1 : 0;
    const bill = row.bills[0];
    barColor = figureColor =
      left < 0
        ? EnumColors.RED
        : due > 0
          ? EnumColors.ORANGE
          : spent > 0
            ? EnumColors.GREEN
            : EnumColors.GREY_1;
    if (barColor === EnumColors.GREY_1) barColor = EnumColors.BLUE;
    figure =
      due > 0
        ? `${money(due)} due`
        : spent > 0
          ? `Paid ${money(spent)}`
          : `${money(planned)} to pay`;
    note =
      due > 0 && bill?.dateDue
        ? `${bill.name} · due ${new Date(bill.dateDue).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`
        : left < 0
          ? `${money(-left)} more than planned`
          : `${money(planned)} planned`;
  } else {
    progress = planned > 0 ? spent / planned : spent > 0 ? 1 : 0;
    barColor =
      left < 0
        ? EnumColors.RED
        : progress > 0.9
          ? EnumColors.ORANGE
          : EnumColors.BLUE;
    if (left < 0) figureColor = EnumColors.RED;
    figure = left < 0 ? `${money(-left)} over` : `${money(left)} left`;
    note = `${money(spent)} of ${money(planned)}`;
  }

  return (
    <button
      type="button"
      data-testid="budget-line"
      data-line-id={row.line.identifier}
      data-active={row.line.id === activePanelNodeId || undefined}
      onClick={() =>
        State.getState().openNodePanel(row.line.id, { autofocus: false })
      }
      className="text-left cursor-pointer rounded-md px-2 py-1.5 -mx-2 flex flex-col gap-1 hover:bg-muted data-active:bg-muted"
    >
      <div className="flex items-baseline gap-2 w-full">
        <Typography
          level="body-md"
          data-testid="budget-line-name"
          className="truncate flex-1 min-w-0"
        >
          <Skeleton loading={isLoading} variant="inline">
            {nameBudgetLine(row.line)}
          </Skeleton>
        </Typography>
        <div data-testid="budget-line-figure" className="shrink-0">
          <Skeleton
            loading={isLoading}
            variant="rectangular"
            className={SKELETON_PIECE_CLASS}
          >
            <SimpleEnum label={figure} enumColor={figureColor} size="sm" />
          </Skeleton>
        </div>
      </div>
      <Skeleton
        loading={isLoading}
        variant="rectangular"
        className={cn(SKELETON_PIECE_CLASS, "w-full h-1.5")}
      >
        <BudgetViewBar value={progress} enumColor={barColor} />
      </Skeleton>
      <Typography
        level="body-xs"
        data-testid="budget-line-note"
        className="truncate text-muted-foreground/80 w-full"
      >
        <Skeleton loading={isLoading} variant="inline">
          {note}
        </Skeleton>
      </Typography>
    </button>
  );
}

/**
 * Last month's leftover, offered once: put toward a goal - so it is not spent
 * by chance - or let be. Either way it is not offered again.
 */
function BudgetViewLeftover({
  budget,
  leftover,
  goals,
  currency,
}: {
  budget: BudgetViewBudget;
  leftover: NonNullable<BudgetViewMonthData["leftover"]>;
  goals: BudgetViewLineMonth[];
  currency?: string;
}) {
  const green = useEnumColor(EnumColors.GREEN);
  const { mutate, loading } = useBackendMutation({
    mutation: Mutations.PUT_BUDGET_LEFTOVER,
    refetchQueries: ["Budgets", "BudgetLineMonth"],
  });
  const put = (lineIdentifier?: string) =>
    mutate({
      variables: {
        input: {
          budgetIdentifier: budget.identifier,
          ...(lineIdentifier ? { lineIdentifier } : {}),
        },
      },
    });
  const name = new Date(`${leftover.month}T12:00:00`).toLocaleDateString(
    undefined,
    { month: "long" },
  );
  return (
    <section
      className={cn(CARD_CLASS, "gap-2 p-4")}
      style={{ borderColor: green.borderColor }}
      aria-label="Last month's leftover"
      data-testid="budget-leftover"
    >
      <Typography level="title-md" data-testid="budget-leftover-said">
        {`${name} ended with ${Utils.fCurrency(leftover.amount, false, false, currency)} you didn't spend`}
      </Typography>
      <Typography level="body-sm" className="text-muted-foreground">
        {goals.length
          ? "Put it toward a goal, so it isn't spent by accident:"
          : "Add a goal in Plan to put leftovers like this toward it."}
      </Typography>
      <div className="flex flex-wrap gap-2">
        {goals.map((row) => (
          <Button
            variant="outline"
            size="sm"
            key={row.line.id}
            disabled={loading}
            data-testid="budget-leftover-goal"
            onClick={() => put(row.line.identifier)}
          >
            {nameBudgetLine(row.line)}
          </Button>
        ))}
        <Button
          variant="ghost"
          size="sm"
          disabled={loading}
          data-testid="budget-leftover-not-now"
          onClick={() => put()}
        >
          Not now
        </Button>
      </div>
    </section>
  );
}
